import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { catchError, debounceTime, distinctUntilChanged, finalize, of, switchMap, tap } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzValidateStatus } from 'ng-zorro-antd/core/types';

import { UserDto } from "../../../dto";
import { HttpUserService } from "../../../services/user/http-user.service";

export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const password = control.get('newPassword')?.value;
    const confirm = control.get('confirmPassword')?.value;
    return password && confirm && password !== confirm ? { passwordMismatch: true } : null;
};

@Component({
    selector: 'app-add-user-modal',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule,
        NzModalModule,
        NzButtonModule,
        NzFormModule,
        NzInputModule
    ],
    templateUrl: './add-user-modal.component.html',
    styleUrls: ['./add-user-modal.component.scss']
})
export class AddUserModalComponent implements OnInit {
    @Input() modalId = 'addUserModal';
    @Output() saved = new EventEmitter<UserDto>();
    @Output() cancelled = new EventEmitter<void>();

    form!: FormGroup;
    
    // UI State
    isVisible = false;
    isConfirmLoading = false; // <--- Controls the Button Spinner
    
    usernameValidateStatus: NzValidateStatus = '';

    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService,
        private httpService: HttpUserService
        // Removed SpinnerService
    ) { }

    ngOnInit(): void {
        this.form = this.fb.group({
            newUsername: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(20)]],
            newPassword: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(20)]],
            confirmPassword: ['', Validators.required],
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(20)]],
            surname: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(20)]],
            email: ['', [Validators.email]],
        }, { validators: passwordMatchValidator });

        this.setupUsernameCheck();
    }

    get confirmPasswordStatus(): NzValidateStatus {
        if (this.form.get('confirmPassword')?.invalid && this.form.get('confirmPassword')?.touched) {
            return 'error';
        }
        if (this.form.hasError('passwordMismatch') && this.form.get('confirmPassword')?.touched) {
            return 'error';
        }
        return '';
    }

    setupUsernameCheck() {
        this.form.get('newUsername')?.valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                tap(() => {
                    if (this.form.get('newUsername')?.valid) {
                         this.usernameValidateStatus = 'validating'; 
                    } else {
                         this.usernameValidateStatus = 'error';
                    }
                }),
                switchMap(value => {
                    if (!value || this.form.get('newUsername')?.invalid) {
                        return of(null);
                    }
                    return this.httpService.checkUsername({ username: value });
                })
            )
            .subscribe(data => {
                const control = this.form.get('newUsername');
                if (!control) return;

                if (!data) {
                    if (control.invalid) this.usernameValidateStatus = 'error';
                    return; 
                }

                if (!data.success) {
                    control.setErrors({ usernameTaken: true });
                    this.usernameValidateStatus = 'error';
                } else {
                    if (control.hasError('usernameTaken')) {
                        delete control.errors?.['usernameTaken'];
                        control.updateValueAndValidity({ onlySelf: true });
                    }
                    this.usernameValidateStatus = 'success';
                }
            });
    }

    open() {
        this.form.reset();
        this.usernameValidateStatus = '';
        this.isVisible = true;
    }

    handleCancel(): void {
        this.isVisible = false;
        this.cancelled.emit();
    }

    save(): void {
        if (this.form.invalid || this.usernameValidateStatus !== 'success') {
            Object.values(this.form.controls).forEach(control => {
                if (control.invalid) {
                    control.markAsDirty();
                    control.updateValueAndValidity({ onlySelf: true });
                }
            });
            this.form.get('confirmPassword')?.markAsTouched();
            return;
        }

        // START BUTTON SPINNER
        this.isConfirmLoading = true; 
        
        const formData = this.form.value;

        const userDto: UserDto = {
            username: formData.newUsername,
            password: formData.newPassword,
            name: formData.name,
            surname: formData.surname,
            email: formData.email
        };

        this.httpService.saveUser(userDto)
            .pipe(
                catchError(error => {
                    console.error('Error adding user', error);
                    this.toastr.error('Failed to add user: ' + (error.error?.error || error.message));
                    throw error;
                }),
                // STOP BUTTON SPINNER (Success or Error)
                finalize(() => this.isConfirmLoading = false) 
            )
            .subscribe(data => {
                if (!data.success) {
                    this.toastr.error('Failed to add user: ' + data.error);
                    return;
                }
                this.toastr.success('User saved successfully');
                this.saved.emit(userDto);
                this.isVisible = false;
            });
    }
}